import { useLocalStorage } from "@mantine/hooks";
import { AuthenticatedComponentGuard } from "@src/auth/authenticated-component-guard";
import {
	SIDEBAR_DEFAULT_WIDTH_PX,
	SIDEBAR_WIDTH_STORAGE_KEY,
} from "@src/components/agent-chat/constants";
import { NavigationToggleButton } from "@src/components/layouts/navigation-toggle-button";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useRef } from "react";
import { useMenuState } from "../menu/menu-drawer";

const SIDEBAR_OCCUPIED_VAR = "--illumi-sidebar-occupied";

export function SideBar(props: {
	children: React.ReactNode;
}) {
	const sidebarDrawer = useMenuState();
	const isDrawerOpen = sidebarDrawer.hasSidebar && sidebarDrawer.isDrawerOpen;

	const [sidebarWidth, setSidebarWidth] = useLocalStorage({
		key: SIDEBAR_WIDTH_STORAGE_KEY,
		defaultValue: `${SIDEBAR_DEFAULT_WIDTH_PX}px`,
	});

	const sidebarRef = useRef<HTMLDivElement>(null);

	// Expose occupied width for agent-chat CSS max-width (no resize listeners).
	useEffect(() => {
		const root = document.documentElement;
		root.style.setProperty(
			SIDEBAR_OCCUPIED_VAR,
			isDrawerOpen ? sidebarWidth : "0px",
		);
		return () => {
			root.style.setProperty(SIDEBAR_OCCUPIED_VAR, "0px");
		};
	}, [isDrawerOpen, sidebarWidth]);

	const handleMouseDown = (e: React.MouseEvent) => {
		e.preventDefault();
		const startX = e.clientX;
		const startWidth = sidebarRef.current?.getBoundingClientRect().width || 0;

		const handleMouseMove = (moveEvent: MouseEvent) => {
			const currentWidth = startWidth + (moveEvent.clientX - startX);
			// Constrain between 220px and 400px
			const clampedWidth = Math.max(220, Math.min(currentWidth, 400));
			setSidebarWidth(`${clampedWidth}px`);
		};

		const handleMouseUp = () => {
			document.removeEventListener("mousemove", handleMouseMove);
			document.removeEventListener("mouseup", handleMouseUp);
			document.body.style.cursor = "default";
		};

		document.addEventListener("mousemove", handleMouseMove);
		document.addEventListener("mouseup", handleMouseUp);
		document.body.style.cursor = "col-resize";
	};

	return (
		<AuthenticatedComponentGuard>
			<AnimatePresence>
				{!isDrawerOpen && (
					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						transition={{ duration: 0.1 }}
						className="fixed z-(--illumi-z-index-navbar-menu) hidden desktop-nav:block"
					>
						{/* Matches board menu spacer (w-10 h-10) when sidebar is closed */}
						<div className="flex h-10 w-10 items-center justify-center leading-none">
							<NavigationToggleButton />
						</div>
					</motion.div>
				)}
			</AnimatePresence>

			<AnimatePresence>
				{isDrawerOpen && (
					<motion.div
						ref={sidebarRef}
						className="relative hidden desktop-nav:block bg-surface-default group/sidebar shrink-0"
						style={{ width: sidebarWidth }}
						initial={{ width: 0 }}
						animate={{ width: sidebarWidth }}
						exit={{ width: 0 }}
						transition={{ duration: 0.1, type: "spring", bounce: 0 }}
					>
						<AnimatePresence>
							{isDrawerOpen && (
								<motion.div
									className="absolute inset-0 block z-(--illumi-z-index-navbar-container) shadow-md h-full w-full"
									initial={{ x: !isDrawerOpen ? "-100%" : 0 }}
									animate={{ x: 0 }}
									exit={{ x: "-100%" }}
									transition={{ duration: 0.1, type: "spring", bounce: 0 }}
								>
									{props.children}
								</motion.div>
							)}
						</AnimatePresence>

						{/* Resize Handle */}
						<div
							onMouseDown={handleMouseDown}
							className="absolute top-0 -right-1 w-2 h-full cursor-col-resize z-(--illumi-z-index-navbar-container) group/handle"
						>
							<div className="flex h-full w-full items-center justify-center opacity-0 transition-all group-hover/sidebar:opacity-100 group-hover/handle:bg-brand-primary/30">
								<div className="h-12 w-[1.5px] rounded-full bg-brand-primary opacity-40 group-hover/handle:opacity-100" />
							</div>
						</div>
					</motion.div>
				)}
			</AnimatePresence>
		</AuthenticatedComponentGuard>
	);
}
